import React, { useState, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, Typography, Button, Descriptions, Row, Col, Space, Spin, Tag, Tabs, message } from 'antd';
import { ArrowLeftOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { getPayrollById, deletePayroll } from '@/api/payroll';
import { useAuth } from '@/contexts/AuthContext';
import DeleteConfirmationModal from '@/components/common/DeleteConfirmationModal';
import type { Payroll } from '@/types/employee/payroll';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

// Type for delete error that matches DeleteConfirmationModal interface
type DeleteError = Error & { 
  dependencies?: Array<{ 
    type: string; 
    items: Array<{ id: string; name?: string; number?: string; title?: string }> 
  }>; 
  blockName?: string; 
  unitNumber?: string; 
  amenityName?: string 
};

const PayrollDetailsPage: React.FC = () => {
  const { t } = useTranslation(['payroll', 'common']);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { firestoreUser } = useAuth();
  
  // State for delete confirmation
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteError, setDeleteError] = useState<DeleteError | undefined>(undefined);
  
  // Get payroll from navigation state or fetch from API
  const state = location.state as { payroll: Payroll; from: string } | null;
  
  const { data: payroll, isLoading } = useQuery({
    queryKey: ['payroll', id],
    queryFn: () => getPayrollById(id!, firestoreUser!),
    enabled: !!id && !state?.payroll && !!firestoreUser?.workspace?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      if (!firestoreUser) throw new Error('User not authenticated');
      return deletePayroll(id, firestoreUser);
    },
    onSuccess: () => {
      message.success(t('payroll:deleteSuccess'));
      queryClient.invalidateQueries({ queryKey: ['payrolls'] });
      setOpenDeleteDialog(false);
      setDeleteError(undefined);
      navigate('/employee/payroll');
    },
    onError: (error: Error) => {
      setDeleteError(error as DeleteError);
      // Don't close modal on error - let DeleteConfirmationModal handle it
    }
  });

  // Use state data if available, otherwise use fetched data
  const displayPayroll = state?.payroll || payroll;

  // Loading state - only show if no state data is available
  if (isLoading && !state?.payroll) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!displayPayroll) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Title level={4}>{t('payroll:notFound')}</Title>
          <Button type="primary" onClick={() => navigate('/employee/payroll')}>
            {t('common:backToList')}
          </Button>
        </div>
      </Card>
    );
  }

  const getStatusTagColor = (status: string) => {
    switch (status) {
      case 'pending': return 'orange';
      case 'processed': return 'green';
      case 'rejected': return 'red';
      default: return 'default';
    }
  };

  const formatMoney = (amount: any) => {
    if (!amount || typeof amount !== 'object') return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: amount.currency || 'USD'
    }).format(amount.amount);
  };

  const handleEdit = () => {
    navigate(`/employee/payroll/${displayPayroll.id}/edit`, { 
      state: { 
        payroll: displayPayroll,
        from: 'details'
      } 
    });
  };

  const handleDelete = useCallback(() => {
    setDeleteError(undefined);
    setOpenDeleteDialog(true);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (displayPayroll.id) {
      deleteMutation.mutate(displayPayroll.id);
    }
  }, [displayPayroll.id, deleteMutation]);

  const handleCancelDelete = () => {
    setOpenDeleteDialog(false);
    setDeleteError(undefined);
  };

  const handleErrorDismiss = () => {
    setDeleteError(undefined);
  };

  const handleBack = () => {
    if (state?.from === 'list') {
      navigate(-1);
    } else {
      navigate('/employee/payroll');
    }
  };

  const tabItems = [
    {
      key: 'details',
      label: t('common:details'),
      children: (
        <div>
          {/* Basic Information */}
          <Descriptions title={t('payroll:basicInformation')} bordered column={2}>
            <Descriptions.Item label={t('payroll:employee')}>
              {displayPayroll.employee.name}
            </Descriptions.Item>
            <Descriptions.Item label={t('payroll:statusLabel')}>
              <Tag color={getStatusTagColor(displayPayroll.status)}>
                {t(`payroll:status.${displayPayroll.status}`)}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label={t('payroll:startDate')}>
              {dayjs(displayPayroll.period.startDate).format('MMM DD, YYYY')}
            </Descriptions.Item>
            <Descriptions.Item label={t('payroll:endDate')}>
              {dayjs(displayPayroll.period.endDate).format('MMM DD, YYYY')}
            </Descriptions.Item>
            <Descriptions.Item label={t('payroll:grossSalary')}>
              <Text strong>{formatMoney(displayPayroll.grossSalary)}</Text>
            </Descriptions.Item>
            <Descriptions.Item label={t('payroll:netSalary')}>
              <Text strong>{formatMoney(displayPayroll.netSalary)}</Text>
            </Descriptions.Item>
            {displayPayroll.payable && (
              <Descriptions.Item label={t('payroll:payable')}>
                {displayPayroll.payable.name}
              </Descriptions.Item>
            )}
          </Descriptions>

          {/* Deductions Section */}
          {displayPayroll.deductions && displayPayroll.deductions.length > 0 && (
            <>
              <div style={{ marginTop: 24 }}>
                <Descriptions title={t('payroll:deductions')} bordered column={1}>
                  {displayPayroll.deductions.map((deduction, index) => (
                    <Descriptions.Item 
                      key={index} 
                      label={`${t(`payroll:deductionTypes.${deduction.type}`)} - ${deduction.description}`}
                    >
                      <Text type="danger">-{formatMoney(deduction.amount)}</Text>
                    </Descriptions.Item>
                  ))}
                </Descriptions>
              </div>
            </>
          )}

          {/* Notes Section */}
          {displayPayroll.notes && (
            <>
              <div style={{ marginTop: 24 }}>
                <Descriptions title={t('payroll:notes')} bordered column={1}>
                  <Descriptions.Item>
                    <Text>{displayPayroll.notes}</Text>
                  </Descriptions.Item>
                </Descriptions>
              </div>
            </>
          )}
        </div>
      ),
    },
    {
      key: 'activity',
      label: t('common:activityLog'),
      children: (
        <div>
          {displayPayroll.activityRecords && displayPayroll.activityRecords.length > 0 ? (
            <div>
              {displayPayroll.activityRecords.map((record, index) => (
                <Card key={index} size="small" style={{ marginBottom: 8 }}>
                  <Row justify="space-between" align="middle">
                    <Col>
                      <div><strong>{t(`common:activityType.${record.type}`)}</strong></div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        {record.performedBy?.displayName || 'Unknown User'}
                      </div>
                    </Col>
                    <Col>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        {dayjs(record.timestamp).format('MMM DD, YYYY HH:mm')}
                      </div>
                    </Col>
                  </Row>
                  {record.changedFields && Object.keys(record.changedFields).length > 0 && (
                    <div style={{ marginTop: 8, fontSize: '12px', color: '#666' }}>
                      <div><strong>Changed Fields:</strong></div>
                      {Object.entries(record.changedFields).map(([field, values]) => (
                        <div key={field}>
                          {field}: {values.oldValue} → {values.newValue}
                        </div>
                      ))}
                    </div>
                  )}
                </Card>
              ))}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
              {t('common:noActivityLog')}
            </div>
          )}
        </div>
      ),
    },
  ];

  return (
    <div style={{ width: '100%', padding: 24 }}>
      {/* Header */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={handleBack}>
              {t('common:back')}
            </Button>
            <Title level={3} style={{ margin: 0 }}>
              {t('payroll:payrollDetails')} - {displayPayroll.employee.name}
            </Title>
          </Space>
        </Col>
        <Col>
          <Space>
            <Button icon={<EditOutlined />} onClick={handleEdit}>
              {t('common:actions.edit')}
            </Button>
            <Button danger icon={<DeleteOutlined />} onClick={handleDelete}>
              {t('common:actions.delete')}
            </Button>
          </Space>
        </Col>
      </Row>

      {/* Content */}
      <Card>
        <Tabs items={tabItems} />
      </Card>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        visible={openDeleteDialog}
        onCancel={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title={t('common:confirmDeleteTitle')}
        error={deleteError}
        onErrorDismiss={handleErrorDismiss}
      />
    </div>
  );
};

export default PayrollDetailsPage; 